<script>
import { defineAsyncComponent } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import WhatsappInteractiveMessageTemplatesAPI from 'dashboard/api/whatsappInteractiveMessageTemplates';

import NextButton from 'dashboard/components-next/button/Button.vue';
import NextSelect from 'dashboard/components-next/select/Select.vue';
import Modal from '../../../../components/Modal.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import AutomationFileInput from 'dashboard/components/widgets/AutomationFileInput.vue';

const EmojiIconPicker = defineAsyncComponent(
  () =>
    import('dashboard/components-next/emoji-icon-picker/EmojiIconPicker.vue')
);

export default {
  name: 'AddCanned',
  components: {
    AutomationFileInput,
    EmojiIconPicker,
    Modal,
    NextButton,
    NextSelect,
    WootMessageEditor,
  },
  props: {
    responseContent: { type: String, default: '' },
    // Suggested (editable) short code, e.g. from the composer selection.
    responseShortCode: { type: String, default: '' },
    cannedResponse: { type: Object, default: null },
    onClose: { type: Function, default: () => {} },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    const response = this.cannedResponse || {};
    const contentAttributes = response.content_attributes || {};
    const whatsappTemplate = contentAttributes.whatsapp_template;

    return {
      shortCode: response.short_code || this.responseShortCode || '',
      content: response.content || this.responseContent || '',
      responseType: response.response_type || 'text',
      attachmentParams: [],
      selectedInboxId: contentAttributes.inbox_id || '',
      selectedTemplateKey: whatsappTemplate
        ? `${whatsappTemplate.name}::${whatsappTemplate.language || ''}`
        : '',
      selectedInteractiveTemplateId:
        contentAttributes.interactive_template?.id || '',
      interactiveTemplates: [],
      isLoadingInteractiveTemplates: false,
      addCanned: { showLoading: false },
      show: true,
      showEmojiPicker: false,
      emojiPickerStyle: {},
    };
  },
  validations: {
    shortCode: { required, minLength: minLength(2) },
    content: { required },
  },
  computed: {
    isEditMode() {
      return Boolean(this.cannedResponse?.id);
    },
    modalTitle() {
      return this.isEditMode
        ? this.$t('CANNED_MGMT.ADD.MODAL.EDIT_TITLE')
        : this.$t('CANNED_MGMT.ADD.MODAL.TITLE');
    },
    modalDescription() {
      return this.isEditMode
        ? this.$t('CANNED_MGMT.ADD.MODAL.EDIT_DESC')
        : this.$t('CANNED_MGMT.ADD.MODAL.DESC');
    },
    submitLabel() {
      if (this.isEditMode) return this.$t('CANNED_MGMT.EDIT.BUTTON_TEXT');

      return this.$t('CANNED_MGMT.ADD.FORM.SUBMIT');
    },
    successMessage() {
      if (this.isEditMode) {
        return this.$t('CANNED_MGMT.EDIT.API.SUCCESS_MESSAGE');
      }

      return this.$t('CANNED_MGMT.ADD.API.SUCCESS_MESSAGE');
    },
    fallbackErrorMessage() {
      if (this.isEditMode) {
        return this.$t('CANNED_MGMT.EDIT.API.ERROR_MESSAGE');
      }

      return this.$t('CANNED_MGMT.ADD.API.ERROR_MESSAGE');
    },
    formId() {
      return `quick-response-${this.cannedResponse?.id || 'create'}-form`;
    },
    existingAttachment() {
      if (
        this.responseType !== 'attachment' ||
        this.cannedResponse?.response_type !== 'attachment'
      ) {
        return null;
      }

      return this.cannedResponse.files?.[0] || null;
    },
    responseTypes() {
      return [
        {
          value: 'text',
          icon: 'i-lucide-message-circle',
          title: this.$t('CANNED_MGMT.ADD.MODAL.TEXT_TITLE'),
          description: this.$t('CANNED_MGMT.ADD.MODAL.TEXT_DESC'),
        },
        {
          value: 'attachment',
          icon: 'i-lucide-paperclip',
          title: this.$t('CANNED_MGMT.ADD.MODAL.ATTACHMENT_TITLE'),
          description: this.$t('CANNED_MGMT.ADD.MODAL.ATTACHMENT_DESC'),
        },
        {
          value: 'whatsapp_template',
          icon: 'i-lucide-badge-check',
          title: this.$t('CANNED_MGMT.ADD.MODAL.APPROVED_TITLE'),
          description: this.$t('CANNED_MGMT.ADD.MODAL.APPROVED_DESC'),
        },
        {
          value: 'whatsapp_interactive',
          icon: 'i-lucide-list-tree',
          title: this.$t('CANNED_MGMT.ADD.MODAL.INTERACTIVE_TITLE'),
          description: this.$t('CANNED_MGMT.ADD.MODAL.INTERACTIVE_DESC'),
        },
      ];
    },
    isTextType() {
      return ['text', 'attachment'].includes(this.responseType);
    },
    inboxes() {
      return this.$store.getters['inboxes/getInboxes'] || [];
    },
    inboxOptions() {
      return this.inboxes
        .filter(inbox => {
          const templates = this.$store.getters[
            'inboxes/getFilteredWhatsAppTemplates'
          ](inbox.id);
          return templates.length > 0;
        })
        .map(inbox => ({ value: inbox.id, label: inbox.name }));
    },
    approvedTemplates() {
      if (!this.selectedInboxId) return [];
      return this.$store.getters['inboxes/getFilteredWhatsAppTemplates'](
        this.selectedInboxId
      );
    },
    approvedTemplateOptions() {
      return this.approvedTemplates.map(template => ({
        value: this.templateKey(template),
        label: `${template.name} · ${template.language || ''}`,
      }));
    },
    selectedWhatsappTemplate() {
      return this.approvedTemplates.find(
        template => this.templateKey(template) === this.selectedTemplateKey
      );
    },
    interactiveTemplateOptions() {
      return this.interactiveTemplates.map(template => ({
        value: template.id,
        label: template.name,
      }));
    },
    selectedInteractiveTemplate() {
      return this.interactiveTemplates.find(
        template =>
          String(template.id) === String(this.selectedInteractiveTemplateId)
      );
    },
    isTypeSelectionInvalid() {
      if (this.responseType === 'attachment') {
        return !this.attachmentParams[0] && !this.existingAttachment;
      }
      if (this.responseType === 'whatsapp_template') {
        return !this.selectedWhatsappTemplate;
      }
      if (this.responseType === 'whatsapp_interactive') {
        return !this.selectedInteractiveTemplate;
      }
      return false;
    },
    isSubmitDisabled() {
      return (
        this.v$.content.$invalid ||
        this.v$.shortCode.$invalid ||
        this.isTypeSelectionInvalid ||
        this.addCanned.showLoading
      );
    },
  },
  watch: {
    selectedInboxId() {
      this.selectedTemplateKey = '';
      if (this.responseType === 'whatsapp_template') this.content = '';
    },
    selectedTemplateKey() {
      if (!this.selectedWhatsappTemplate) return;
      this.content = this.whatsappTemplateBody(this.selectedWhatsappTemplate);
    },
    selectedInteractiveTemplateId() {
      this.content = this.selectedInteractiveTemplate?.content || '';
    },
  },
  mounted() {
    this.$store.dispatch('inboxes/get');
    this.fetchInteractiveTemplates();
  },
  methods: {
    selectResponseType(type) {
      if (type === this.responseType) return;

      this.responseType = type;
      this.content = type === 'text' ? this.responseContent || '' : '';
      this.attachmentParams = [];
      this.selectedInboxId = '';
      this.selectedTemplateKey = '';
      this.selectedInteractiveTemplateId = '';
      this.v$.content.$reset();
    },
    templateKey(template) {
      return `${template.name}::${template.language || ''}`;
    },
    whatsappTemplateBody(template) {
      const body = (template.components || []).find(
        component => component.type?.toUpperCase() === 'BODY'
      );
      return body?.text || template.name || '';
    },
    async fetchInteractiveTemplates() {
      this.isLoadingInteractiveTemplates = true;
      try {
        const { data } = await WhatsappInteractiveMessageTemplatesAPI.get();
        this.interactiveTemplates = Array.isArray(data) ? data : [];
      } catch {
        this.interactiveTemplates = [];
      } finally {
        this.isLoadingInteractiveTemplates = false;
      }
    },
    buildContentAttributes() {
      if (this.responseType === 'whatsapp_template') {
        return {
          inbox_id: Number(this.selectedInboxId),
          whatsapp_template: this.selectedWhatsappTemplate,
        };
      }
      if (this.responseType === 'whatsapp_interactive') {
        return { interactive_template: this.selectedInteractiveTemplate };
      }
      return {};
    },
    async addCannedResponse() {
      this.v$.$touch();
      if (this.isSubmitDisabled) return;

      this.addCanned.showLoading = true;
      try {
        const cannedResponse = {
          short_code: this.shortCode,
          content: this.content,
          response_type: this.responseType,
          content_attributes: this.buildContentAttributes(),
        };
        if (this.attachmentParams[0]) {
          cannedResponse.attachment_blob_id = this.attachmentParams[0];
        }

        const action = this.isEditMode
          ? 'updateCannedResponse'
          : 'createCannedResponse';
        const payload = this.isEditMode
          ? {
              id: this.cannedResponse.id,
              canned_response: cannedResponse,
            }
          : { canned_response: cannedResponse };

        await this.$store.dispatch(action, payload);
        useAlert(this.successMessage);
        this.onClose();
      } catch (error) {
        useAlert(error?.message || this.fallbackErrorMessage);
      } finally {
        this.addCanned.showLoading = false;
      }
    },
    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker;
      if (this.showEmojiPicker) {
        this.$nextTick(this.positionEmojiPicker);
      }
    },
    positionEmojiPicker() {
      const button = this.$refs.emojiButton;
      if (!button) return;

      const buttonRect = button.getBoundingClientRect();
      const pickerWidth = 352;
      const pickerHeight = 380;
      const viewportGap = 16;
      const buttonGap = 8;
      const left = Math.max(
        viewportGap,
        Math.min(
          buttonRect.right - pickerWidth,
          window.innerWidth - pickerWidth - viewportGap
        )
      );
      const fitsBelow =
        buttonRect.bottom + buttonGap + pickerHeight <=
        window.innerHeight - viewportGap;
      const top = fitsBelow
        ? buttonRect.bottom + buttonGap
        : Math.max(viewportGap, buttonRect.top - pickerHeight - buttonGap);

      this.emojiPickerStyle = {
        left: `${left}px`,
        top: `${top}px`,
      };
    },
    hideEmojiPicker() {
      this.showEmojiPicker = false;
    },
    insertEmoji({ value }) {
      emitter.emit(BUS_EVENTS.INSERT_INTO_RICH_EDITOR, value);
      this.showEmojiPicker = false;
    },
  },
};
</script>

<template>
  <Modal
    v-model:show="show"
    :on-close="onClose"
    size="quick-response-modal-shell"
  >
    <div class="quick-response-modal">
      <header class="quick-response-header">
        <span class="quick-response-header__icon">
          <span class="i-lucide-message-square-text" />
        </span>
        <div class="min-w-0">
          <h2>{{ modalTitle }}</h2>
          <p>{{ modalDescription }}</p>
        </div>
      </header>

      <nav
        class="response-tools"
        :aria-label="$t('CANNED_MGMT.ADD.MODAL.FORMATS_ARIA')"
      >
        <button
          v-for="type in responseTypes"
          :key="type.value"
          type="button"
          class="response-tool"
          :class="{ 'response-tool--active': responseType === type.value }"
          @click="selectResponseType(type.value)"
        >
          <span class="response-tool__icon">
            <span :class="type.icon" />
          </span>
          <span class="min-w-0">
            <strong>{{ type.title }}</strong>
            <small>{{ type.description }}</small>
          </span>
          <span
            v-if="responseType === type.value"
            class="i-lucide-check response-tool__status"
          />
        </button>
      </nav>

      <form
        :id="formId"
        class="quick-response-form"
        @submit.prevent="addCannedResponse"
      >
        <div class="quick-response-body">
          <section class="quick-response-section">
            <div class="section-heading">
              <span class="i-lucide-command" />
              <div>
                <h3>{{ $t('CANNED_MGMT.ADD.MODAL.IDENTIFICATION_TITLE') }}</h3>
                <p>{{ $t('CANNED_MGMT.ADD.MODAL.IDENTIFICATION_DESC') }}</p>
              </div>
            </div>
            <label :class="{ error: v$.shortCode.$error }">
              {{ $t('CANNED_MGMT.ADD.FORM.SHORT_CODE.LABEL') }}
              <div class="short-code-field">
                <span>{{ $t('CANNED_MGMT.ADD.MODAL.SHORT_CODE_PREFIX') }}</span>
                <input
                  v-model="shortCode"
                  type="text"
                  :placeholder="
                    $t('CANNED_MGMT.ADD.FORM.SHORT_CODE.PLACEHOLDER')
                  "
                  @blur="v$.shortCode.$touch"
                />
              </div>
            </label>
          </section>

          <section v-if="isTextType" class="quick-response-section">
            <div class="section-heading section-heading--action">
              <span class="i-lucide-text-cursor-input" />
              <div class="flex-1">
                <h3>{{ $t('CANNED_MGMT.ADD.FORM.CONTENT.LABEL') }}</h3>
                <p>{{ $t('CANNED_MGMT.ADD.MODAL.MESSAGE_DESC') }}</p>
              </div>
              <div class="emoji-action">
                <button
                  ref="emojiButton"
                  v-tooltip.top="$t('CANNED_MGMT.ADD.MODAL.EMOJI_TOOLTIP')"
                  type="button"
                  class="emoji-action__button"
                  :aria-expanded="showEmojiPicker"
                  @click.stop="toggleEmojiPicker"
                >
                  <span class="i-lucide-smile-plus" />
                  {{ $t('CANNED_MGMT.ADD.MODAL.EMOJI') }}
                </button>
                <Teleport to="body">
                  <EmojiIconPicker
                    v-if="showEmojiPicker"
                    v-on-clickaway="hideEmojiPicker"
                    mode="emoji"
                    class="emoji-action__picker"
                    :style="emojiPickerStyle"
                    @select="insertEmoji"
                  />
                </Teleport>
              </div>
            </div>
            <div class="editor-wrap">
              <WootMessageEditor
                v-model="content"
                class="message-editor"
                :class="{ editor_warning: v$.content.$error }"
                channel-type="Context::Default"
                enable-variables
                :enable-canned-responses="false"
                :placeholder="$t('CANNED_MGMT.ADD.FORM.CONTENT.PLACEHOLDER')"
                @blur="v$.content.$touch"
              />
            </div>
            <div v-if="responseType === 'attachment'" class="attachment-field">
              <div>
                <strong>{{
                  $t('CANNED_MGMT.ADD.MODAL.ATTACHMENT_FIELD')
                }}</strong>
                <small>{{ $t('CANNED_MGMT.ADD.MODAL.ATTACHMENT_HELP') }}</small>
              </div>
              <AutomationFileInput
                v-model="attachmentParams"
                :initial-file-name="existingAttachment?.filename || ''"
              />
            </div>
          </section>

          <section
            v-else-if="responseType === 'whatsapp_template'"
            class="quick-response-section native-selection"
          >
            <div class="section-heading">
              <span class="i-lucide-badge-check" />
              <div>
                <h3>{{ $t('CANNED_MGMT.ADD.MODAL.SELECT_TEMPLATE_TITLE') }}</h3>
                <p>{{ $t('CANNED_MGMT.ADD.MODAL.SELECT_TEMPLATE_DESC') }}</p>
              </div>
            </div>
            <label>
              {{ $t('CANNED_MGMT.ADD.MODAL.INBOX_LABEL') }}
              <NextSelect
                v-model="selectedInboxId"
                class="w-full [&>select]:w-full"
                :options="inboxOptions"
                :placeholder="$t('CANNED_MGMT.ADD.MODAL.INBOX_PLACEHOLDER')"
              />
            </label>
            <label>
              {{ $t('CANNED_MGMT.ADD.MODAL.TEMPLATE_LABEL') }}
              <NextSelect
                v-model="selectedTemplateKey"
                class="w-full [&>select]:w-full"
                :options="approvedTemplateOptions"
                :disabled="!selectedInboxId"
                :placeholder="$t('CANNED_MGMT.ADD.MODAL.TEMPLATE_PLACEHOLDER')"
              />
            </label>
            <div v-if="selectedWhatsappTemplate" class="selection-preview">
              <span class="i-lucide-message-square-text" />
              <p>{{ content }}</p>
            </div>
          </section>

          <section v-else class="quick-response-section native-selection">
            <div class="section-heading">
              <span class="i-lucide-list-tree" />
              <div>
                <h3>
                  {{ $t('CANNED_MGMT.ADD.MODAL.SELECT_INTERACTIVE_TITLE') }}
                </h3>
                <p>{{ $t('CANNED_MGMT.ADD.MODAL.SELECT_INTERACTIVE_DESC') }}</p>
              </div>
            </div>
            <label>
              {{ $t('CANNED_MGMT.ADD.MODAL.INTERACTIVE_LABEL') }}
              <NextSelect
                v-model="selectedInteractiveTemplateId"
                class="w-full [&>select]:w-full"
                :options="interactiveTemplateOptions"
                :disabled="isLoadingInteractiveTemplates"
                :placeholder="
                  $t('CANNED_MGMT.ADD.MODAL.INTERACTIVE_PLACEHOLDER')
                "
              />
            </label>
            <div v-if="selectedInteractiveTemplate" class="selection-preview">
              <span class="i-lucide-message-square-text" />
              <p>{{ content }}</p>
            </div>
          </section>
        </div>
      </form>

      <footer class="quick-response-footer">
        <p>
          <span class="i-lucide-info" />
          {{ $t('CANNED_MGMT.ADD.MODAL.FOOTER_HINT') }}
        </p>
        <div class="flex justify-end gap-2">
          <NextButton
            faded
            slate
            type="button"
            :label="$t('CANNED_MGMT.ADD.CANCEL_BUTTON_TEXT')"
            @click="onClose"
          />
          <NextButton
            type="submit"
            :form="formId"
            :label="submitLabel"
            :disabled="isSubmitDisabled"
            :is-loading="addCanned.showLoading"
          />
        </div>
      </footer>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
:deep(.quick-response-modal-shell) {
  width: min(52rem, calc(100vw - 2rem)) !important;
  max-width: calc(100vw - 2rem) !important;
  overflow: hidden !important;
}

.quick-response-modal {
  @apply flex max-h-[calc(100vh-2rem)] flex-col overflow-hidden rounded-xl bg-n-surface-1;
}

.quick-response-header {
  @apply flex shrink-0 items-start gap-3 border-b border-n-weak px-5 py-4;

  h2 {
    @apply m-0 text-lg font-semibold text-n-slate-12;
  }
  p {
    @apply mb-0 mt-0.5 text-sm text-n-slate-10;
  }
}

.quick-response-header__icon {
  @apply grid size-10 shrink-0 place-items-center rounded-xl bg-n-brand/15 text-n-brand ring-1 ring-inset ring-n-brand/20;

  > span {
    @apply size-5;
  }
}

.response-tools {
  @apply grid shrink-0 grid-cols-2 gap-2 border-b border-n-weak bg-n-alpha-1 p-4 lg:grid-cols-4;
}

.response-tool {
  @apply grid min-w-0 grid-cols-[auto_1fr_auto] items-center gap-2 rounded-xl border border-n-weak bg-n-surface-1 p-2.5 text-left transition-colors hover:border-n-brand;

  strong,
  small {
    @apply block truncate;
  }
  strong {
    @apply text-sm font-medium text-n-slate-12;
  }
  small {
    @apply mt-0.5 text-[11px] text-n-slate-10;
  }
}

.response-tool__icon {
  @apply grid size-8 place-items-center rounded-lg bg-n-alpha-3 text-n-slate-11 ring-1 ring-inset ring-n-weak transition-colors;

  > span {
    @apply size-5;
  }
}

.response-tool--active {
  @apply border-n-brand bg-n-brand/5;

  .response-tool__icon {
    @apply bg-n-brand/15 text-n-brand ring-n-brand/25;
  }

  .response-tool__status {
    @apply text-n-brand;
  }
}

.quick-response-form {
  @apply flex min-h-0 w-full flex-1 flex-col;
}

.quick-response-body {
  @apply grid min-h-0 flex-1 gap-3 overflow-y-auto p-4 md:grid-cols-[17rem_minmax(0,1fr)];
}

.quick-response-section {
  @apply min-w-0 rounded-xl border border-n-weak bg-n-surface-1 p-4;
}

.section-heading {
  @apply mb-3 flex items-start gap-2.5;

  > span {
    @apply mt-0.5 text-base text-n-brand;
  }
  h3 {
    @apply m-0 text-sm font-semibold text-n-slate-12;
  }
  p {
    @apply mb-0 mt-0.5 text-xs text-n-slate-10;
  }
}

.section-heading--action {
  @apply items-center;
}

.short-code-field {
  @apply mt-1 flex h-10 items-center overflow-hidden rounded-lg border border-n-weak bg-n-surface-1 transition-colors focus-within:border-n-brand;

  > span {
    @apply grid h-full w-9 shrink-0 place-items-center text-base font-semibold text-n-slate-9;
  }
  input {
    @apply min-w-0 flex-1 px-1.5 text-sm text-n-slate-12;
    height: 100% !important;
    margin: 0 !important;
    border: 0 !important;
    border-radius: 0 !important;
    outline: 0 !important;
    background: transparent !important;
    box-shadow: none !important;
  }
}

label.error .short-code-field {
  @apply border-n-ruby-9;
}

.editor-wrap {
  @apply overflow-hidden rounded-lg border border-n-weak bg-n-alpha-1;
}

.emoji-action {
  @apply relative ml-auto;
}
.emoji-action__button {
  @apply flex items-center gap-1.5 rounded-lg bg-n-alpha-2 px-2.5 py-1.5 text-xs font-medium text-n-slate-11 hover:text-n-brand;
}
.emoji-action__picker {
  position: fixed !important;
  z-index: 10060 !important;
}

.attachment-field {
  @apply mt-3 grid items-center gap-3 rounded-lg border border-dashed border-n-strong bg-n-alpha-1 p-3 sm:grid-cols-[1fr_minmax(12rem,auto)];

  strong,
  small {
    @apply block;
  }
  strong {
    @apply text-xs font-semibold text-n-slate-12;
  }
  small {
    @apply mt-0.5 text-[11px] text-n-slate-10;
  }
}

.native-selection {
  @apply flex flex-col gap-3;
}
.native-selection label {
  @apply flex flex-col gap-1 text-xs font-medium text-n-slate-12;
}
.selection-preview {
  @apply flex gap-2 rounded-lg border border-n-weak bg-n-alpha-1 p-3 text-n-slate-11;
}
.selection-preview p {
  @apply mb-0 max-h-24 overflow-y-auto whitespace-pre-wrap text-xs;
}

.quick-response-footer {
  @apply flex w-full shrink-0 items-center justify-between gap-4 border-t border-n-weak bg-n-alpha-1 px-5 py-3;

  p {
    @apply mb-0 flex items-center gap-2 text-xs text-n-slate-10;
  }
}

:deep(.ProseMirror-menubar) {
  @apply hidden;
}
:deep(.ProseMirror-woot-style) {
  @apply min-h-24 max-h-40 overflow-y-auto px-3 py-2;
}

@media (max-width: 640px) {
  .quick-response-footer {
    @apply items-stretch flex-col;
  }
  .quick-response-footer p {
    @apply hidden;
  }
}
</style>
